import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TablePagination,
  InputAdornment,
  InputLabel,
  Tooltip
} from "@mui/material";
import { AiOutlineClose, AiOutlineSearch, AiOutlineInfoCircle } from "react-icons/ai";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { PiFileCsv, PiFileArrowDown } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";
import { fetchMissingContractBreakdown, fetchMissingContractInvoices } from "../../api/dashboardApi";
import dayjs from "dayjs";
import { AiOutlineEye } from "react-icons/ai";
import DataTable from "../DataTable/DataTable";

const InvoiceListModal = ({ open, onClose, docId, serviceId, sofStatus, formatNumber }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["missingContractInvoices", docId, serviceId, sofStatus],
    queryFn: () => fetchMissingContractInvoices({ doc_id: docId, service_id: serviceId, sof_status: sofStatus }),
    enabled: open && !!docId && !!serviceId && !!sofStatus,
  });

  const invoices = data?.data || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Invoices for Doc: {docId} | Service: {serviceId}
        </Box>
        <IconButton onClick={onClose} size="small">
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: "#7C2EA0" }} />
          </Box>
        ) : isError ? (
          <Typography color="error" textAlign="center">
            {error?.message || "Failed to load invoices."}
          </Typography>
        ) : invoices.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <DataTable
              height={450}
              rows={invoices.map((inv, idx) => ({ 
                ...inv, 
                id: inv.invoice_id + "_" + idx,
                sof_status: inv.sof_status ? inv.sof_status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : "",
                status: inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : "",
                discrepancy_amount: inv.discrepancy_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                total_adj: inv.total_adj?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                total_discrepancy: inv.total_discrepancy?.toLocaleString(undefined, { minimumFractionDigits: 2 }),
              }))}
              columns={[
                { field: "invoice_id", headerName: "Invoice ID", width: 140 },
                { field: "invoice_date", headerName: "Date", width: 120 },
                { field: "customer_id", headerName: "Customer ID", width: 130 },
                { field: "account_no", headerName: "Account No", width: 120 },
                { field: "sof_status", headerName: "SOF Status", width: 150 },
                { field: "discrepancy_amount", headerName: "Discrepancy Amount", width: 220 },
                // { field: "total_adj", headerName: "Adjustment", width: 110 },
                // { field: "total_discrepancy", headerName: "Total Discrepancy", width: 160 },
                // { field: "status", headerName: "Status", width: 110 },
                {
                  field: "action",
                  headerName: "Action",
                  sortable: false,
                  width: 80,
                  renderCell: (params) => (
                    <IconButton
                      size="small"
                      onClick={() => setPreviewUrl(params.row.enc_name)}
                      sx={{ color: "#7C2EA0" }}
                    >
                      <AiOutlineEye size={18} />
                    </IconButton>
                  ),
                },
              ]}
            />
          </Box>
        ) : (
          <Typography textAlign="center" color="textSecondary" sx={{ fontStyle: "italic", p: 2 }}>
            No invoices found.
          </Typography>
        )}

        <Dialog 
          open={!!previewUrl} 
          onClose={() => setPreviewUrl(null)} 
          fullWidth 
          maxWidth="lg"
          PaperProps={{ sx: { height: "90vh" } }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
            Document Preview
            <IconButton onClick={() => setPreviewUrl(null)} size="small">
              <AiOutlineClose />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
            <iframe
              src={previewUrl}
              width="100%"
              height="100%"
              title="Invoice PDF Preview"
              style={{ border: "none" }}
            />
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ row, onInvoiceCountClick }) => {
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "#fcf9ff" },
        }}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: "#7C2EA0" }}
          >
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
          {row.doc_id}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, color: "#7C2EA0" }}>
          {row.total_discrepancy?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {row.total_unique_services}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                backgroundColor: "#fdfbff",
                borderRadius: "8px",
                p: 1,
                border: "1px solid #f0e6f5",
              }}
            >
              <TableContainer sx={{ maxHeight: 250, overflow: "auto", borderRadius: "4px" }}>
                <Table size="small" aria-label="services" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}>
                        Service ID
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        Discrepancy Amount
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        Invoice Count
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "12px", backgroundColor: "#F8EAFF", color: "#7C2EA0" }}
                      >
                        SOF Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(row.services || []).map((service, index) => (
                      <TableRow
                        key={index}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: "12px" }}>
                          {service.service_id}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          {service.discrepancy_amount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ 
                            fontSize: "12px", 
                            fontWeight: 600, 
                            color: "#7C2EA0",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "#5a1f7a" }
                          }}
                          onClick={() => onInvoiceCountClick(row.doc_id, service.service_id)}
                        >
                          {service.invoice_count}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: "12px", fontWeight: 500 }}
                        >
                          <Box
                            component="span"
                            sx={{
                              backgroundColor: service.sof_status === "SERVICE_IN_SOF" ? "#e8f5e9" : "#ffebee",
                              color: service.sof_status === "SERVICE_IN_SOF" ? "#2e7d32" : "#c62828",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "10px",
                              fontWeight: 600
                            }}
                          >
                            {service.sof_status}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const MissingContractBreakdownModal = ({
  open,
  onClose,
  initialFilters,
  sofStatus,
  formatNumber,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [invoiceModalProps, setInvoiceModalProps] = useState({ open: false, docId: null, serviceId: null });
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [isDownloadingDetailed, setIsDownloadingDetailed] = useState(false);

  const handleInvoiceCountClick = (docId, serviceId) => {
    setInvoiceModalProps({ open: true, docId, serviceId });
  };

  const handleReset = () => {
    setSearchQuery("");
    setPage(0);
  };

  const currentFilters = {
    vendor_id: initialFilters.vendor_id,
    sof_status: sofStatus
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["missingContractBreakdown", currentFilters],
    queryFn: () => fetchMissingContractBreakdown(currentFilters),
    enabled: open && !!sofStatus,
  });

  const breakdownData = data?.data || [];

  // Filter based on search (account no / doc_id)
  const filteredData = breakdownData.filter((item) =>
    (item.doc_id || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalDiscrepancy = breakdownData.reduce((acc, curr) => acc + (curr.total_discrepancy || 0), 0);

  const handleDownloadSummaryCSV = async () => {
    if (!breakdownData.length) return;
    setIsDownloadingSummary(true);
    try {
      // Define columns for CSV
      const headers = ["Document No", "Total Discrepancy", "Total Services"];
      const csvRows = [headers.join(",")];

      breakdownData.forEach(row => {
        const values = [
          `"${row.doc_id}"`,
          row.total_discrepancy,
          row.total_unique_services
        ];
        csvRows.push(values.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const fileName = sofStatus === "SERVICE_NOT_IN_SOF" 
        ? `Due_To_Missing_Contract_Summary_${dayjs().format("YYYYMMDD")}.csv` 
        : `Based_On_Contract_Amount_Summary_${dayjs().format("YYYYMMDD")}.csv`;
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Summary CSV Download Error:", err);
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  const handleDownloadDetailedCSV = async () => {
    setIsDownloadingDetailed(true);
    try {
      const csvData = await fetchMissingContractInvoices({ 
        sof_status: sofStatus, 
        is_csv: true 
      });

      if (!csvData) return;

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const fileName = sofStatus === "SERVICE_NOT_IN_SOF" 
        ? `Due_To_Missing_Contract_Detailed_${dayjs().format("YYYYMMDD")}.csv` 
        : `Based_On_Contract_Amount_Detailed_${dayjs().format("YYYYMMDD")}.csv`;
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Detailed CSV Download Error:", err);
    } finally {
      setIsDownloadingDetailed(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#333", fontSize: "1.1rem" }}>
              {sofStatus === "SERVICE_NOT_IN_SOF" ? "Due to Missing Contract" : "Based on Contract Amount"}
            </Typography>
            <Tooltip 
              title={
                sofStatus === "SERVICE_NOT_IN_SOF" 
                  ? "The Invoice Count represents the number of invoices in which a particular service ID is present but cannot be found within the corresponding document (SOF). This indicates that the service has been billed in invoices without a matching reference in the contract document. The Total Services denotes the number of such unmatched or missing services associated with that specific document number. These values are derived through the invoice matching process, where invoice data is compared against the uploaded SOFs to identify discrepancies and missing service mappings."
                  : "The Invoice Count represents the number of invoices in which a particular service ID is present and successfully matches with the corresponding document (SOF). The Total Services (e.g., 95) reflects the total number of such matched service entries identified for that document."
              }
              arrow 
              placement="top"
            >
              <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#7C2EA0" }}>
                <AiOutlineInfoCircle size={18} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* 🔹 Filter Bar */}
        <Box
          sx={{
            mb: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            overflowX: "auto",
            pb: 1,
            gap: 2,
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e0e0e0",
              borderRadius: "10px",
            },
          }}
        >
          {/* Stats on Left */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
             {breakdownData.length > 0 && (
              <Tooltip 
                title={`Total Discrepancy: ${totalDiscrepancy.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
                arrow 
                placement="top"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#fff",
                    color: "#7C2EA0",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    border: "1px solid #7C2EA0",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
                  }}
                >
                  <Typography component="span" sx={{ opacity: 0.7, fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Discrepancy:</Typography>
                  <Box component="span" sx={{ fontSize: "0.9rem" }}>
                    {formatNumber ? formatNumber(totalDiscrepancy) : totalDiscrepancy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Box>
                </Box>
              </Tooltip>
            )}
            {breakdownData.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#fff",
                  color: "#7C2EA0",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "20px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  border: "1px solid #7C2EA0",
                  boxShadow: "0 2px 4px rgba(124, 46, 160, 0.1)"
                }}
              >
                <Typography component="span" sx={{ opacity: 0.7, fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>Count:</Typography>
                <Box component="span" sx={{ fontSize: "0.9rem" }}>{breakdownData.length}</Box>
              </Box>
            )}
            
           
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search Document No..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 200,
              "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AiOutlineSearch size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {searchQuery !== "" && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    paddingInline: "10px",
                    height: "32px",
                  }}
                >
                  Reset
                </Button>
              )}
              
              <Tooltip title="Download Table CSV" placement="top" arrow>
                <span
                  onClick={!isDownloadingSummary ? handleDownloadSummaryCSV : undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: (breakdownData.length > 0 && !isDownloadingSummary) ? "pointer" : "default",
                    opacity: (breakdownData.length > 0) ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (breakdownData.length > 0 && !isDownloadingSummary) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  {isDownloadingSummary ? (
                    <CircularProgress size={16} sx={{ color: "#333" }} />
                  ) : (
                    <PiFileCsv size={18} color="#333" />
                  )}
                </span>
              </Tooltip>

              <Tooltip title="Download Detailed CSV" placement="top" arrow>
                <span
                  onClick={!isDownloadingDetailed ? handleDownloadDetailedCSV : undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: (breakdownData.length > 0 && !isDownloadingDetailed) ? "pointer" : "default",
                    opacity: (breakdownData.length > 0) ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (breakdownData.length > 0 && !isDownloadingDetailed) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  {isDownloadingDetailed ? (
                    <CircularProgress size={16} sx={{ color: "#7C2EA0" }} />
                  ) : (
                    <PiFileArrowDown size={21} color="#7C2EA0" />
                  )}
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Box>

        <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {error?.message || "Failed to load missing contract breakdown."}
              </Typography>
            </Box>
          ) : filteredData.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "45vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50, zIndex: 11 }} />
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Document No
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Total Discrepancy
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Total Services
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((row, index) => (
                      <Row 
                        key={row.doc_id || index} 
                        row={row} 
                        onInvoiceCountClick={handleInvoiceCountClick} 
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No data available matching your search.
              </Typography>
            </Box>
          )}
        </Box>
        <InvoiceListModal
          open={invoiceModalProps.open}
          onClose={() => setInvoiceModalProps(prev => ({ ...prev, open: false }))}
          docId={invoiceModalProps.docId}
          serviceId={invoiceModalProps.serviceId}
          sofStatus={sofStatus}
          formatNumber={formatNumber}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MissingContractBreakdownModal;
